import React, { useState, useEffect, useRef, memo } from 'react';
import {
    IconHeart,
    IconShare3,
    IconBookmark,
    IconBookmarkFilled,
    IconMessageCircle,
    IconFlag,
    IconDots,
    IconPuzzle,
    IconPencil,
    IconTrash
} from '@tabler/icons-react';
import ActionButton from '../../../components/common/ActionButton.jsx';

function PostFooter({
    postId,
    postLikeStatus,
    fallbackLikeCount,
    handlePostLike,
    handleMessageClick,
    handleShare,
    shareCount = 0,
    isSaved,
    handleSave,
    currentUser,
    // commentsByPostId, // REMOVED
    commentCount,
    solutionsCount = 0,
    onSolutionsClick,
    showSolutions = false,
    handleReport,
    showCommentBox,
    // New props for owner actions
    canManage = false,
    onEditPost = () => {},
    onDeletePost = () => {},
}) {
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const menuRef = useRef(null);
    const moreBtnRef = useRef(null);

    // Close on outside click and Escape key
    useEffect(() => {
        if (!showOptionsMenu) return;

        const handleClickOutside = (e) => {
            const menuEl = menuRef.current;
            const btnEl = moreBtnRef.current;
            if (!menuEl || !btnEl) return;
            if (!menuEl.contains(e.target) && !btnEl.contains(e.target)) {
                setShowOptionsMenu(false);
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setShowOptionsMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showOptionsMenu]);

    const likeCountDisplay = typeof postLikeStatus?.likes === 'number'
        ? postLikeStatus.likes
        : (typeof fallbackLikeCount === 'number' ? fallbackLikeCount : 0);

    return (
        <div className="post__footer">
            <div className="postButtons">
                <ActionButton
                  onClick={handlePostLike}
                  icon={<IconHeart size={20} fill={postLikeStatus.isLiked ? '#31C65B' : 'none'} stroke={postLikeStatus.isLiked ? '#31C65B' : 'currentColor'} />}
                  text="Like"
                  count={likeCountDisplay}
                  active={postLikeStatus.isLiked}
                  disabled={postLikeStatus.isLikeProcessing}
                  ariaLabel={postLikeStatus.isLiked ? 'Unlike post' : 'Like post'}
                  title={postLikeStatus.isLiked ? 'Unlike' : 'Like'}
                />

                <ActionButton
                  onClick={handleMessageClick}
                  icon={<IconMessageCircle size={20} />}
                  text="Comment"
                  count={commentCount || 0}
                  active={showCommentBox}
                  className={`post-footer-comment-button ${showCommentBox ? 'active' : ''}`}
                  ariaLabel="Comment on post"
                  title="Comment"
                />

                <ActionButton
                  onClick={handleShare}
                  icon={<IconShare3 size={20} />}
                  text="Share"
                  count={typeof shareCount === 'number' ? shareCount : 0}
                  ariaLabel="Share post"
                  title="Share"
                />

                <ActionButton
                  onClick={onSolutionsClick}
                  icon={<IconPuzzle size={20} />}
                  text="Solutions"
                  count={typeof solutionsCount === 'number' ? solutionsCount : 0}
                  active={showSolutions}
                  aria-expanded={showSolutions}
                  aria-controls={`solutions-${postId}`}
                  ariaLabel={showSolutions ? 'Hide solutions' : 'Show solutions'}
                  title="Solutions"
                />

                <ActionButton
                  onClick={handleSave}
                  icon={isSaved ? (<IconBookmarkFilled size={20} stroke={"#31C65B"} />) : (<IconBookmark size={20} />)}
                  text="Save"
                  className={isSaved ? 'saved' : ''}
                  ariaLabel={isSaved ? 'Unsave post' : 'Save post'}
                  title={isSaved ? 'Unsave' : 'Save'}
                  aria-pressed={isSaved}
                />

                <div className="post-options-menu-container">
                    <button
                        type="button"
                        className="post-action-button"
                        onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                        aria-label="More options"
                        aria-haspopup="menu"
                        aria-expanded={showOptionsMenu}
                        title="More"
                        ref={moreBtnRef}
                    >
                        <IconDots size={20} />
                    </button>
                    <div
                        className="post-options-menu"
                        data-visible={showOptionsMenu}
                        role="menu"
                        ref={menuRef}
                    >
                        {canManage && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onEditPost();
                                        setShowOptionsMenu(false);
                                    }}
                                >
                                    <IconPencil size={16} />
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onDeletePost();
                                        setShowOptionsMenu(false);
                                    }}
                                >
                                    <IconTrash size={16} />
                                    Delete
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                handleReport();
                                setShowOptionsMenu(false);
                            }}
                        >
                            <IconFlag size={16} />
                            Report
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default memo(PostFooter);